/**
 * liveSyncEngine.js — Rê Leve
 *
 * Sync em tempo real entre abas do mesmo origin (cliente ↔ operador).
 * Triplo fallback para máxima compatibilidade:
 *   1. BroadcastChannel  — moderno, instantâneo (Chrome/Firefox/Safari 16+)
 *   2. storage event     — funciona em todos os browsers, cross-tab
 *   3. CustomEvent       — funciona dentro da MESMA aba (mesma janela)
 *
 * API:
 *   publish(eventType, payload)
 *   subscribe(eventType, handler)  → retorna unsubscribe
 *   subscribeAll(handler)          → recebe TODOS os eventos
 *
 * Tipos de evento padronizados (constantes):
 *   chat.message.user          → pergunta do cliente
 *   chat.message.bot           → resposta da assistente
 *   order.closed               → kit confirmado
 *   order.upsell.added         → cliente aceitou upsell (suco/sobremesa)
 *   order.cross_sell.added     → cliente aceitou cross-sell
 *   approval.pending.created   → assistente encaminhou pra equipe
 *   approval.answered          → equipe respondeu pergunta pendente
 *   knowledge.rag.updated      → entrada RAG criada/editada/removida
 *   knowledge.guardrail.toggled
 *   knowledge.context.updated
 *   sync.status                → ping de "tô vivo"
 */

const CHANNEL_NAME = 'releve-live-sync';
const LS_BROADCAST_KEY = 'releve.broadcast'; // chave usada só pra disparar storage event
const REPLAY_BUFFER = 'releve.eventBuffer';
const MAX_BUFFER = 200;

// ── Event types como constantes para evitar typos ──────────────────────────
export const EVT = {
  CHAT_USER:               'chat.message.user',
  CHAT_BOT:                'chat.message.bot',
  ORDER_CLOSED:            'order.closed',
  ORDER_UPSELL_ADDED:      'order.upsell.added',
  ORDER_CROSS_SELL_ADDED:  'order.cross_sell.added',
  APPROVAL_PENDING:        'approval.pending.created',
  APPROVAL_ANSWERED:       'approval.answered',
  KNOWLEDGE_RAG_UPDATED:   'knowledge.rag.updated',
  KNOWLEDGE_GUARDRAIL_TOGGLED: 'knowledge.guardrail.toggled',
  KNOWLEDGE_CONTEXT_UPDATED: 'knowledge.context.updated',
  SYNC_STATUS:             'sync.status',
};

// ── Canal de broadcast ─────────────────────────────────────────────────────
let _channel = null;
function getChannel() {
  if (typeof window === 'undefined') return null;
  if (_channel) return _channel;
  try {
    if ('BroadcastChannel' in window) {
      _channel = new BroadcastChannel(CHANNEL_NAME);
    }
  } catch { _channel = null; }
  return _channel;
}

// ── Subscribers locais (dentro da mesma aba) ───────────────────────────────
const _handlers = new Set();   // [{type, fn}]
const _allHandlers = new Set();

function deliverLocally(evt) {
  _allHandlers.forEach(fn => { try { fn(evt); } catch {} });
  _handlers.forEach(({ type, fn }) => {
    if (evt.type === type) { try { fn(evt); } catch {} }
  });
}

// ── Listeners cross-tab ────────────────────────────────────────────────────
let _initialized = false;
function initOnce() {
  if (_initialized || typeof window === 'undefined') return;
  _initialized = true;

  // 1. BroadcastChannel
  const ch = getChannel();
  if (ch) {
    ch.addEventListener('message', (e) => {
      if (e.data && e.data.__releveSync) deliverLocally(e.data.event);
    });
  }

  // 2. storage event (cross-tab fallback)
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_BROADCAST_KEY || !e.newValue) return;
    try {
      const env = JSON.parse(e.newValue);
      if (env && env.event) deliverLocally(env.event);
    } catch {}
  });
}

// ── Buffer de replay (para o painel que abre depois reconstituir histórico) ─
function appendBuffer(evt) {
  if (typeof window === 'undefined') return;
  try {
    const buf = JSON.parse(localStorage.getItem(REPLAY_BUFFER) || '[]');
    buf.unshift(evt);
    localStorage.setItem(REPLAY_BUFFER, JSON.stringify(buf.slice(0, MAX_BUFFER)));
  } catch {}
}

export function getBuffer({ limit = 200, type = null } = {}) {
  if (typeof window === 'undefined') return [];
  try {
    let buf = JSON.parse(localStorage.getItem(REPLAY_BUFFER) || '[]');
    if (type) buf = buf.filter(e => e.type === type);
    return buf.slice(0, limit);
  } catch { return []; }
}

export function clearBuffer() {
  if (typeof window === 'undefined') return;
  try { localStorage.removeItem(REPLAY_BUFFER); } catch {}
  publish(EVT.SYNC_STATUS, { action: 'buffer-cleared' });
}

// ── Publish ────────────────────────────────────────────────────────────────
export function publish(type, payload = {}) {
  initOnce();
  const event = {
    id: 'evt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
    type,
    payload,
    ts: new Date().toISOString(),
    timestamp: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };

  // Persist no buffer (replay)
  appendBuffer(event);

  // 1. Local (mesma aba)
  deliverLocally(event);

  // 2. BroadcastChannel (outras abas, instantâneo)
  const ch = getChannel();
  if (ch) {
    try { ch.postMessage({ __releveSync: true, event }); } catch {}
  }

  // 3. storage event (fallback cross-tab) — escreve e apaga pra forçar evento mesmo se valor for igual
  if (typeof window !== 'undefined') {
    try {
      const envelope = JSON.stringify({ at: Date.now(), event });
      localStorage.setItem(LS_BROADCAST_KEY, envelope);
    } catch {}
  }

  return event;
}

// ── Subscribe ──────────────────────────────────────────────────────────────
export function subscribe(type, fn) {
  initOnce();
  const entry = { type, fn };
  _handlers.add(entry);
  return () => _handlers.delete(entry);
}

export function subscribeAll(fn) {
  initOnce();
  _allHandlers.add(fn);
  return () => _allHandlers.delete(fn);
}

// ── Status helper ──────────────────────────────────────────────────────────
export function getSyncStatus() {
  const hasBC = typeof window !== 'undefined' && 'BroadcastChannel' in window;
  const hasSW = typeof window !== 'undefined' && 'serviceWorker' in navigator;
  return {
    broadcastChannel: hasBC,
    serviceWorker: hasSW,
    bufferSize: getBuffer().length,
    initialized: _initialized,
  };
}

// ── Auto-init quando módulo é carregado ────────────────────────────────────
if (typeof window !== 'undefined') initOnce();
